import { useState, useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

const LostRevenueCalculator = () => {
  const [industry, setIndustry] = useState('Home Services');
  const [callsPerDay, setCallsPerDay] = useState(15);
  const [missedPercent, setMissedPercent] = useState(30);
  const [avgValue, setAvgValue] = useState(250);
  const [closeRate, setCloseRate] = useState(40);
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    phone: ''
  });

  // Calculate results
  const callsPerMonth = callsPerDay * 30;
  const missedCalls = Math.round(callsPerMonth * (missedPercent / 100));
  const wonJobs = Math.round(missedCalls * (closeRate / 100));
  const lostMonthly = wonJobs * avgValue;
  const lostYearly = lostMonthly * 12;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getInsightMessage = () => {
    if (lostMonthly < 1000) {
      return "Even small leaks add up over time.";
    } else if (lostMonthly < 5000) {
      return "That's a real monthly marketing budget—being lost to missed calls.";
    } else {
      return "You're likely losing a full-time employee's worth of revenue each month.";
    }
  };

  const getIndustryMessage = () => {
    const messages: Record<string, string> = {
      'Home Services': 'Most homeowners call 2–3 businesses before choosing.',
      'Medical/Dental': 'Missed calls often mean missed patients.',
      'Legal': 'Potential clients rarely wait—they call the next firm.',
      'Real Estate': 'In real estate, speed to lead is everything.',
      'Auto Services': 'Urgent calls often go to the first shop that answers.',
      'Beauty/Wellness': 'Clients book with whoever responds first.',
      'Other': 'First to answer often means first to win the business.'
    };
    return messages[industry] || messages['Other'];
  };

  const getValueLabel = () => {
    if (industry === 'Legal') {
      return 'Average case or consultation value ($)';
    }
    return 'Average job / appointment value ($)';
  };

  const scrollToCalculator = () => {
    const element = document.getElementById('calculator');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prepare webhook payload
    const webhookPayload = {
      email: formData.email,
      firstName: formData.firstName,
      phone: formData.phone || '',
      industry: industry,
      avgValue: avgValue,
      callsPerDay: callsPerDay,
      closeRate: closeRate,
      missedPercent: missedPercent,
      missedCallsPerMonth: missedCalls,
      lostMonthly: lostMonthly,
      lostYearly: lostYearly
    };

    // Send to webhook (fire-and-forget)
    fetch('https://services.leadconnectorhq.com/hooks/ufGgg1PmwgWzIDRrG0qr/webhook-trigger/bf860990-ac40-43e1-a060-5e0105860b8b', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(webhookPayload)
    }).catch(() => {
      // Silently ignore webhook errors
    });

    // Original form submission for Readdy
    const formDataToSend = new FormData();
    
    formDataToSend.append('firstName', formData.firstName);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('phone', formData.phone);
    formDataToSend.append('industry', industry);
    formDataToSend.append('callsPerDay', callsPerDay.toString());
    formDataToSend.append('missedPercent', missedPercent.toString());
    formDataToSend.append('avgValue', avgValue.toString());
    formDataToSend.append('closeRate', closeRate.toString());
    formDataToSend.append('missedCallsPerMonth', missedCalls.toString());
    formDataToSend.append('lostMonthly', formatCurrency(lostMonthly));
    formDataToSend.append('lostYearly', formatCurrency(lostYearly));

    try {
      await fetch('https://readdy.ai/api/form/d5bipgfdcu97hjrn3qbg', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      
      setFormSubmitted(true);
      setFormData({ firstName: '', email: '', phone: '' });
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-white to-pink-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Find Out How Much Missed Calls Are Costing You.
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-4">
            In 60 seconds, estimate the revenue you're losing each month when calls go unanswered.
          </p>
          <p className="text-sm text-gray-600 mb-8">
            Conservative estimates. Built for small business owners.
          </p>
          <button
            onClick={scrollToCalculator}
            className="whitespace-nowrap px-8 py-4 bg-pink-500 hover:bg-pink-600 text-white text-lg font-semibold rounded-lg transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl"
          >
            Calculate My Lost Revenue
          </button>
        </div>
      </section>

      {/* Calculator Section */}
      <section id="calculator" className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Inputs */}
              <div className="space-y-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">Your Business Details</h2>
                
                {/* Industry */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Industry
                  </label>
                  <select
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:outline-none text-base cursor-pointer"
                  >
                    <option>Home Services</option>
                    <option>Medical/Dental</option>
                    <option>Legal</option>
                    <option>Real Estate</option>
                    <option>Auto Services</option>
                    <option>Beauty/Wellness</option>
                    <option>Other</option>
                  </select>
                </div>

                {/* Calls Per Day */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Average calls per day
                  </label>
                  <input
                    type="number"
                    value={callsPerDay}
                    onChange={(e) => setCallsPerDay(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:outline-none text-base"
                    min="1"
                  />
                </div>

                {/* Missed Percent */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Estimated % of calls missed: {missedPercent}%
                  </label>
                  <input
                    type="range"
                    value={missedPercent}
                    onChange={(e) => setMissedPercent(parseInt(e.target.value))}
                    min="0"
                    max="80"
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-pink-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>80%</span>
                  </div>
                </div>

                {/* Average Value */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    {getValueLabel()}
                  </label>
                  <input
                    type="number"
                    value={avgValue}
                    onChange={(e) => setAvgValue(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:outline-none text-base"
                    min="1"
                  />
                </div>

                {/* Close Rate */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Close rate from calls: {closeRate}%
                  </label>
                  <input
                    type="range"
                    value={closeRate}
                    onChange={(e) => setCloseRate(parseInt(e.target.value))}
                    min="0"
                    max="100"
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-pink-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-8 flex flex-col justify-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">Your Lost Revenue</h2>
                
                <div className="space-y-6">
                  <div className="bg-white rounded-lg p-6 shadow-md">
                    <p className="text-sm font-semibold text-gray-600 mb-2">Missed Calls Per Month</p>
                    <p className="text-4xl font-bold text-pink-600">{missedCalls.toLocaleString()}</p>
                  </div>

                  <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-6 shadow-lg border-2 border-pink-200">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Lost Revenue Per Month</p>
                    <p className="text-5xl font-extrabold text-pink-600">{formatCurrency(lostMonthly)}</p>
                  </div>

                  <div className="bg-white rounded-lg p-6 shadow-md">
                    <p className="text-sm font-semibold text-gray-600 mb-2">Lost Revenue Per Year</p>
                    <p className="text-4xl font-bold text-pink-600">{formatCurrency(lostYearly)}</p>
                    <p className="text-xs text-gray-500 mt-3 italic">
                      This estimate assumes just one missed opportunity per caller — many businesses lose more.
                    </p>
                  </div>

                  <div className="bg-pink-100 border-l-4 border-pink-500 p-4 rounded">
                    <p className="text-sm font-medium text-gray-800 italic">
                      {getInsightMessage()}
                    </p>
                  </div>

                  <div className="bg-purple-100 border-l-4 border-purple-500 p-4 rounded">
                    <p className="text-sm font-medium text-gray-800">
                      {getIndustryMessage()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Get the Report Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
              Email Me My Full Report
            </h2>
            
            {/* What Happens Next Section */}
            <div className="mb-8">
              <p className="text-sm font-semibold text-gray-600 mb-4 text-center uppercase tracking-wide">
                What Happens Next?
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">You get your results</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">We show how to stop the leak</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">You try it live (no commitment)</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-lg text-gray-700 mb-8 text-center">
              We'll send your results plus a simple plan to stop missing calls.
            </p>

            {!formSubmitted ? (
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:outline-none text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:outline-none text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone (optional)
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:outline-none text-base"
                  />
                </div>

                <button
                  type="submit"
                  className="whitespace-nowrap w-full px-8 py-4 bg-pink-500 hover:bg-pink-600 text-white text-lg font-semibold rounded-lg transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl"
                >
                  Send My Report
                </button>
              </form>
            ) : (
              <div className="text-center space-y-6">
                <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Report Sent!</h3>
                  <p className="text-lg text-gray-700">
                    Want to see it live? Call the demo line anytime:
                  </p>
                </div>
                <a
                  href="tel:+18886009252"
                  className="whitespace-nowrap inline-block px-8 py-4 bg-pink-500 hover:bg-pink-600 text-white text-lg font-semibold rounded-lg transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl"
                >
                  Try the Live Demo: +1 888-600-9252
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why This Matters Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-timer-flash-line text-3xl text-pink-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Speed Wins</h3>
              <p className="text-gray-700">
                Most callers choose the first business that answers.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-phone-line text-3xl text-pink-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Missed Calls = Missed Leads</h3>
              <p className="text-gray-700">
                Voicemails don't convert like conversations.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-checkbox-circle-line text-3xl text-pink-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Every Call Captured</h3>
              <p className="text-gray-700">
                Reception Genie answers, qualifies, and routes instantly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Reception Genie Solves It */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            How Reception Genie Solves It
          </h2>
          
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-fill text-4xl text-pink-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Answer every call instantly</h3>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-search-line text-4xl text-pink-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Capture name + reason for calling</h3>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-route-line text-4xl text-pink-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Route to the right person or book next step</h3>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-3-line text-4xl text-pink-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Log every lead + follow up automatically</h3>
            </div>
          </div>

          <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl shadow-2xl p-12 text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Stop the leak. Capture every call.
            </h2>
            <a
              href="tel:+18886009252"
              className="whitespace-nowrap inline-block px-8 py-4 bg-white text-pink-600 text-lg font-semibold rounded-lg transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl hover:scale-105"
            >
              Try the Live Demo
            </a>
            <p className="text-lg mt-4 opacity-90">
              Call anytime: +1 888-600-9252
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <details className="bg-white rounded-xl shadow-lg p-6 cursor-pointer group">
              <summary className="text-xl font-bold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                Is this calculator accurate?
                <i className="ri-arrow-down-s-line text-2xl text-pink-600 group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed">
                The calculator uses conservative industry estimates. Your actual numbers may vary, but this gives you a realistic baseline of what missed calls could be costing your business each month.
              </p>
            </details>

            <details className="bg-white rounded-xl shadow-lg p-6 cursor-pointer group">
              <summary className="text-xl font-bold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                What if I don't know my missed call rate?
                <i className="ri-arrow-down-s-line text-2xl text-pink-600 group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed">
                Most small businesses miss 20-40% of incoming calls during busy periods, after hours, or when staff is occupied. Start with 30% as a baseline—you can always adjust it based on your specific situation.
              </p>
            </details>

            <details className="bg-white rounded-xl shadow-lg p-6 cursor-pointer group">
              <summary className="text-xl font-bold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                Does Reception Genie work after hours?
                <i className="ri-arrow-down-s-line text-2xl text-pink-600 group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed">
                Yes! Reception Genie works 24/7/365. Whether it's 2 PM or 2 AM, every call gets answered professionally. You never miss an opportunity, even when you're closed.
              </p>
            </details>

            <details className="bg-white rounded-xl shadow-lg p-6 cursor-pointer group">
              <summary className="text-xl font-bold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                Can it book appointments or take messages?
                <i className="ri-arrow-down-s-line text-2xl text-pink-600 group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed">
                Absolutely. Reception Genie can book appointments directly into your calendar, take detailed messages, answer common questions, and route urgent calls to the right person—all automatically.
              </p>
            </details>

            <details className="bg-white rounded-xl shadow-lg p-6 cursor-pointer group">
              <summary className="text-xl font-bold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                How fast can I get set up?
                <i className="ri-arrow-down-s-line text-2xl text-pink-600 group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed">
                Most businesses are up and running within 24-48 hours. We handle the technical setup, train the AI on your business, and make sure everything works perfectly before you go live.
              </p>
            </details>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LostRevenueCalculator;
